import { buildCaptureStream } from './audio';
import { isKioskShell } from '../config/kioskShell';
import { ClipRecorder, type RecorderOptions } from './recorder';
import { noteError, noteEvent } from '../lib/diagnostics';

/**
 * Rolling pre-roll, so the clip opens a beat BEFORE the caw.
 *
 * A clip that starts on the gesture starts on a guest already mid-caw, with no
 * sense of the change having been caused by anything. The payoff reads far
 * better with a moment of them as they were in front of it.
 *
 * You cannot ask MediaRecorder for the past, and you cannot trim the front off
 * what it gives you: with `start(timeslice)` the first blob welds the container
 * header to the first slice of picture. Drop it and the file will not play;
 * keep it and the clip opens on a stray moment from whenever the camera went
 * live, then jumps. Splitting the two means parsing EBML or fragmented MP4 by
 * hand, per browser.
 *
 * So the pre-roll comes from a recorder that was ALREADY RUNNING. Slots run in
 * staggered cycles, and at the trigger we keep whichever has been running
 * longest and throw the rest away. The kept one is an ordinary unbroken
 * recording that simply began before the trigger did — no parsing, no
 * concatenation, no second code path for the file to be wrong in.
 *
 * The cost of the trick is that the pre-roll is a window rather than a number.
 * With two slots the survivor's age lands between half a cycle and a full one;
 * with one — the kiosk, see `slotCount()` — it lands anywhere from zero to a
 * full cycle, and zero means the caller records from the trigger instead.
 * `claim()` logs what it actually got.
 *
 * Slots share ONE capture stream. Calling `captureStream()` twice on a canvas is
 * specified to hand back independent tracks, but a cancelled recorder stopping a
 * track another is still recording from is a silent, mid-session,
 * device-specific failure — and sharing one stream removes the question
 * entirely.
 */

/**
 * Slot count, and on the kiosk this is the single most expensive number in the
 * app.
 *
 * Two leaves no gap — while one recycles the other is always mid-cycle — and
 * that is worth having wherever encoding is cheap. On the RK3568 panel it is
 * not: the board has no VP8/VP9 hardware encoder, so every slot is a software
 * encode of 720x1280 running for the whole live phase. Measured 2026-09-18, on
 * the capture screen, 2.5s apart:
 *
 *   before the pre-roll starts   60.5 fps
 *   with two slots running        7.0 fps
 *
 * Camera Kit, MediaPipe and the compositor were innocent — they held 60fps on
 * their own. The encoders alone took the live preview to a slideshow.
 *
 * One slot on the kiosk. The cost is a gap: for the moment a slot is being
 * recycled there is nothing rolling, and a trigger landing in that window gets
 * no lead-in. `CaptureScreen` already handles that — `claim()` returning null
 * falls through to recording from the trigger — so the failure is a missing
 * 1.2s of lead-in on an unlucky take, not a lost clip.
 *
 * Read lazily rather than at module scope: `window.__KIOSK__` is spliced in
 * ahead of the bundle so a constant would in fact be correct, but nothing here
 * needs to depend on that import-order guarantee.
 */
function slotCount(): number {
  return isKioskShell() ? 1 : 2;
}

export class PreRollRecorder {
  private slots: ClipRecorder[] = [];
  private stream: MediaStream | null = null;
  private timer: number | null = null;
  private options: RecorderOptions | null = null;

  get running(): boolean {
    return this.slots.length > 0;
  }

  /**
   * @param cycleMs how long a slot records before being recycled. The claimed
   *   pre-roll lands between `cycleMs / slotCount()` and `cycleMs`, and is
   *   simply `cycleMs` when there is only one slot.
   */
  start(options: RecorderOptions, cycleMs: number): boolean {
    if (this.running) return true;

    try {
      this.stream = buildCaptureStream(options.canvas, options.micStream, options.fps ?? 30);
    } catch (err) {
      // Not fatal: the caller falls back to recording from the trigger, which
      // is what it did before any of this existed.
      noteError('preroll.stream', err);
      return false;
    }

    const shared: RecorderOptions = { ...options, stream: this.stream };
    this.options = shared;

    const first = new ClipRecorder();
    if (!first.start(shared)) {
      this.cancel();
      return false;
    }
    this.slots = [first];

    const slots = slotCount();
    // `cycleMs` states the lead-in we want, and how a cycle maps onto it depends
    // on the slot count. With two staggered slots the survivor's age lands in
    // [cycle/2, cycle]; with one it lands anywhere in [0, cycle], so the same
    // cycle would halve the average lead-in. Widen it to keep the mean near what
    // the caller asked for rather than quietly delivering half.
    const effectiveCycleMs = slots === 1 ? cycleMs * 2 : cycleMs;
    const stagger = Math.max(100, Math.round(effectiveCycleMs / slots));
    this.timer = window.setInterval(() => this.tick(), stagger);
    noteEvent(
      `pre-roll rolling (${slots} slot${slots === 1 ? '' : 's'}, ${effectiveCycleMs}ms cycle)`,
    );
    return true;
  }

  private tick(): void {
    const options = this.options;
    if (!options) return;

    // Stagger the slots into life one tick apart, which is what puts them out
    // of phase and keeps a usable pre-roll available at every moment after.
    if (this.slots.length < slotCount()) {
      const next = new ClipRecorder();
      if (next.start(options)) this.slots.push(next);
      return;
    }

    this.oldest()?.restart();
  }

  /** The slot with the most picture behind it. */
  private oldest(): ClipRecorder | null {
    let best: ClipRecorder | null = null;
    for (const slot of this.slots) {
      if (!slot.recordingSince) continue;
      if (!best || slot.recordingSince < best.recordingSince) best = slot;
    }
    return best;
  }

  /**
   * Stop cycling, keep the longest-running recorder and discard the rest.
   *
   * The survivor is still recording when it is handed back — it has been since
   * before the trigger, which is the whole point — so the caller stops it at
   * the end of the reveal exactly as it would have stopped one it started
   * itself.
   */
  claim(): ClipRecorder | null {
    this.clearTimer();
    // A slot caught mid-recycle reports no `recordingSince`, so `oldest()` skips
    // it. With one slot that is the whole pre-roll, and returning null here would
    // send the caller down its trigger-frame fallback — building a captureStream
    // and a MediaRecorder on the very frame the effect fires, which is the hitch
    // this class exists to move off it. A reopening slot is back within a task
    // turn and yields a valid clip with ~no lead-in, which is strictly better.
    const keep = this.oldest() ?? this.slots.find((slot) => slot.reopening) ?? null;

    for (const slot of this.slots) {
      if (slot !== keep) slot.cancel();
    }
    this.slots = [];
    this.options = null;

    if (!keep) {
      this.releaseStream();
      return null;
    }

    const since = keep.recordingSince;
    noteEvent(
      since
        ? `pre-roll claimed — ${Date.now() - since}ms before the trigger`
        : 'pre-roll claimed mid-recycle — no lead-in',
    );
    // The survivor outlives this object, so the shared stream goes with it.
    keep.assumeStreamOwnership();
    this.stream = null;
    return keep;
  }

  /** Abandon everything — teardown, retake, idle reset, or a failed start. */
  cancel(): void {
    this.clearTimer();
    this.slots.forEach((slot) => slot.cancel());
    this.slots = [];
    this.options = null;
    this.releaseStream();
  }

  private releaseStream(): void {
    this.stream?.getVideoTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}
